import React, { useState, useEffect, useRef } from 'react';

const AnimatedCounter = ({ value, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) {
      let startTime;
      const endValue = parseFloat(value.toString().replace(/[^0-9.]/g, ''));

      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);

        setCount(Math.floor(progress * endValue));

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    }
  }, [isVisible, value, duration]);

  return (
    <span ref={elementRef} className="stat-number" style={{
      fontSize: '3rem',
      fontWeight: '700',
      color: '#FF6B6B'
    }}>
      {count.toLocaleString()}{suffix}
    </span>
  );
};

const Problem = () => {
  return (
    <section id="problem" className="section-padding technical-lines">
      <div className="container-max px-4">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <p className="text-xs font-bold tracking-widest text-rebel-coral uppercase mb-4 font-sans" style={{maxWidth: 'none'}}>
            THE CYCLE OF FOSSIL FUEL DEPENDENCY
          </p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-semibold mb-16 text-rebel-dark leading-tight">
            The problem(s)
          </h2>

          {/* Problem 1 */}
          <div className="w-full max-w-4xl mx-auto mb-12">
            <div className="flex items-center mb-6">
              <div className="flex-grow border-t border-gray-300"></div>
              <h3 className="px-6 text-2xl sm:text-3xl font-bold text-rebel-dark whitespace-nowrap">The Distress Purchase Cycle</h3>
              <div className="flex-grow border-t border-gray-300"></div>
            </div>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed w-full" style={{maxWidth: 'none'}}>
              Most fossil fuel boilers are replaced with <strong>another fossil fuel boiler</strong>. A fossil fuel boiler is a <strong>distress purchase</strong>. When your boiler breaks, you need heat <strong>now</strong>. No time to assess heat pump readiness. That locks in fossil fuel dependence and volatile energy costs for another <strong>15 years</strong>. The climate doesn't have time for that either.
            </p>
          </div>

          {/* Problem 2 */}
          <div className="w-full max-w-4xl mx-auto mb-12">
            <div className="flex items-center mb-6">
              <div className="flex-grow border-t border-gray-300"></div>
              <h3 className="px-6 text-2xl sm:text-3xl font-bold text-rebel-dark whitespace-nowrap">Outdated Heat Loss Surveys</h3>
              <div className="flex-grow border-t border-gray-300"></div>
            </div>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed w-full" style={{maxWidth: 'none'}}>
              Many heat loss surveys are <strong>inaccurate</strong>, <strong>expensive</strong> and often <strong>do not capture the usage pattern of the building</strong>. They require a specialist to visit your property, and can add hundreds of pounds to the cost of a heat pump installation. For older heritage buildings, these surveys are particularly challenging due to intermittent heating patterns and thermal mass, leading to systems that are oversized and wasteful, or undersized and unable to deliver the expected heat output. Traditional surveys rarely capture real-world usage, and the calculations often rely on spreadsheets with incorrect assumptions (like steady-state heat loss).
            </p>
          </div>
        </div>

        {/* Game Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {/* Stat Card 1 */}
          <div className="text-center game-card p-8">
            <div className="mb-4">
              <AnimatedCounter value="24,000,000" suffix="" />
            </div>
            <h3 className="text-xl font-semibold text-rebel-dark mb-2">UK homes</h3>
            <p className="text-gray-600">heated by fossil gas</p>
          </div>

          {/* Stat Card 2 */}
          <div className="text-center game-card p-8">
            <div className="mb-4">
              <AnimatedCounter value="1,500,000" suffix="" />
            </div>
            <h3 className="text-xl font-semibold text-rebel-dark mb-2">Fossil fuel boilers</h3>
            <p className="text-gray-600">replaced annually in UK</p>
          </div>

          {/* Stat Card 3 */}
          <div className="text-center game-card p-8">
            <div className="mb-4">
              <AnimatedCounter value="33" suffix="%" />
            </div>
            <h3 className="text-xl font-semibold text-rebel-dark mb-2">Carbon emissions</h3>
            <p className="text-gray-600">from UK heating systems</p>
          </div>
        </div>

      </div>
    </section>
  );
};

export default Problem;